var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { json } from "@hyper-hyper-space/hhs3_json";
import { refAdvanceFormat, extractRefVersion, validateRefAdvanceMonotonicity, validationFailure, validationOk, wrapValidationFailure, } from "@hyper-hyper-space/hhs3_mvt";
import { verifyPayloadSignature, isAuthoredPayload, extractAuthor } from "../../authorship.js";
import { createSetFormat, addElmtFormat, addElmtAuthoredFormat, deleteElmtFormat, deleteElmtAuthoredFormat, updateElmtFormat, } from "./payload.js";
import { hashElement } from "./hash.js";
export function validateRSetPayload(payload, context) {
    return __awaiter(this, void 0, void 0, function* () {
        if (context.mode === "create") {
            return validateCreatePayload(payload, context.parent);
        }
        return validateOpPayload(payload, context.set, context.at);
    });
}
function validateCreatePayload(payload, parent) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!json.checkFormat(createSetFormat, payload)) {
            return validationFailure("RSet create payload format is invalid");
        }
        const createPayload = payload;
        if (createPayload.parent !== undefined && parent !== undefined) {
            if (createPayload.parent !== parent.getId()) {
                return validationFailure("RSet create parent does not match nesting parent");
            }
        }
        if (createPayload.contentType === undefined && createPayload.acceptUpdateForDeleted !== undefined) {
            return validationFailure("acceptUpdateForDeleted only makes sense if contentType is present");
        }
        if (createPayload.contentType !== undefined && createPayload.acceptRedundantAdd !== undefined) {
            return validationFailure("acceptRedundantAdd only makes sense if contentType is not present");
        }
        if (createPayload.contentType !== undefined) {
            if (createPayload.initialElements.length > 0) {
                return validationFailure("initialElements must be empty if contentType is present");
            }
        }
        if (createPayload.supportBarrierAdd && createPayload.supportBarrierDelete) {
            return validationFailure("supportBarrierAdd and supportBarrierDelete are mutually exclusive");
        }
        if (createPayload.capabilityRef !== undefined) {
            const reqs = createPayload.capRequirements;
            if (reqs === undefined)
                return validationFailure("permissioned RSet must declare capability requirements");
            if (reqs.add === undefined && reqs.delete === undefined)
                return validationFailure("permissioned RSet must require add or delete capability");
        }
        else if (createPayload.capRequirements !== undefined) {
            return validationFailure("capRequirements only make sense when capabilityRef is present");
        }
        return validationOk();
    });
}
function validateOpPayload(payload, rset, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (typeof payload !== "object" || payload === null || Array.isArray(payload))
            return validationFailure("RSet payload must be an object");
        if (typeof payload["action"] !== "string")
            return validationFailure("RSet payload action must be a string");
        const action = payload["action"];
        switch (action) {
            case "add":
                return validateAddPayload(payload, rset, at);
            case "delete":
                return validateDeletePayload(payload, rset, at);
            case "update":
                return validateUpdatePayload(payload, rset, at);
            case "ref-advance":
                return validateRefAdvancePayload(payload, rset, at);
            default:
                return validationFailure(`unknown RSet action '${action}'`);
        }
    });
}
function validateAddPayload(payload, rset, at) {
    return __awaiter(this, void 0, void 0, function* () {
        const format = rset.isPermissioned() ? addElmtAuthoredFormat : addElmtFormat;
        if (!json.checkFormat(format, payload))
            return validationFailure("RSet add payload format is invalid");
        const addPayload = payload;
        if (!rset.supportBarrierAdd() && json.hasKey(addPayload, "barrier"))
            return validationFailure("barrier add is not supported");
        if (!rset.acceptRedundantAdd()) {
            const view = yield rset.getView(at, at);
            if (yield view.hasByHash(hashElement(addPayload.element)))
                return validationFailure("element already exists in set");
        }
        if (rset.isPermissioned()) {
            return checkPayloadAuth(payload, rset, at, rset.capRequirementForAdd());
        }
        return validationOk();
    });
}
function validateDeletePayload(payload, rset, at) {
    return __awaiter(this, void 0, void 0, function* () {
        const format = rset.isPermissioned() ? deleteElmtAuthoredFormat : deleteElmtFormat;
        if (!json.checkFormat(format, payload))
            return validationFailure("RSet delete payload format is invalid");
        const deletePayload = payload;
        if (!rset.supportBarrierDelete() && json.hasKey(deletePayload, "barrier"))
            return validationFailure("barrier delete is not supported");
        if (!rset.acceptRedundantDelete()) {
            const view = yield rset.getView(at, at);
            if (!(yield view.hasByHash(deletePayload.elementHash)))
                return validationFailure(`element '${deletePayload.elementHash}' does not exist in set`);
        }
        if (rset.isPermissioned()) {
            return checkPayloadAuth(payload, rset, at, rset.capRequirementForDelete());
        }
        return validationOk();
    });
}
function validateRefAdvancePayload(payload, rset, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!rset.isPermissioned())
            return validationFailure("ref-advance is only valid for permissioned sets");
        if (!json.checkFormat(refAdvanceFormat, payload, { strict: false }))
            return validationFailure("RSet ref-advance payload format is invalid");
        if (!isAuthoredPayload(payload))
            return validationFailure("RSet ref-advance must be authored");
        const refPayload = payload;
        if (refPayload.refId !== rset.capabilityRef())
            return validationFailure(`ref-advance ref '${refPayload.refId}' is not the set capability ref`);
        const rcap = yield rset.loadRCap();
        if (rcap === undefined)
            return validationFailure("permissioned set capability object is not available");
        const newRefVersion = extractRefVersion(refPayload);
        const observerDag = yield rset.getScopedDag();
        const referencedDag = yield rcap.getCausalDag();
        if (!(yield validateRefAdvanceMonotonicity(observerDag, referencedDag, refPayload.refId, newRefVersion, at))) {
            return validationFailure("RSet ref-advance is not monotonic");
        }
        if (!(yield verifyPayloadSignature(payload, (keyId) => rcap.lookupKey(keyId)))) {
            return validationFailure("RSet ref-advance signature could not be verified");
        }
        const authorId = extractAuthor(payload);
        if (rset.refAdvanceCreators() && rcap.isCreator(authorId))
            return validationOk();
        const rsetView = yield rset.getView(at, at);
        const rcapVersion = yield rsetView.resolveRefVersion(rset.capabilityRef());
        const rcapView = yield rcap.getView(rcapVersion, rcapVersion);
        for (const cap of rset.refAdvanceCaps()) {
            if (yield rcapView.hasCapability(authorId, cap))
                return validationOk();
        }
        return validationFailure(`author '${authorId}' is not authorized to advance the capability ref`);
    });
}
function checkPayloadAuth(payload, rset, at, capName) {
    return __awaiter(this, void 0, void 0, function* () {
        const rcap = yield rset.loadRCap();
        if (rcap === undefined)
            return validationFailure("permissioned set capability object is not available");
        if (!(yield verifyPayloadSignature(payload, (keyId) => rcap.lookupKey(keyId)))) {
            return validationFailure("RSet payload signature could not be verified");
        }
        if (capName !== undefined) {
            const authorId = extractAuthor(payload);
            const rsetView = yield rset.getView(at, at);
            const rcapVersion = yield rsetView.resolveRefVersion(rset.capabilityRef());
            const rcapView = yield rcap.getView(rcapVersion, rcapVersion);
            if (!(yield rcapView.hasCapability(authorId, capName)))
                return validationFailure(`author '${authorId}' does not have capability '${capName}'`);
        }
        return validationOk();
    });
}
function validateUpdatePayload(payload, rset, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!json.checkFormat(updateElmtFormat, payload)) {
            return validationFailure("RSet update payload format is invalid");
        }
        if (rset.contentType() === undefined) {
            return validationFailure("RSet update requires contentType");
        }
        const updatePayload = payload;
        if (!rset.acceptUpdateForDeleted()) {
            const view = yield rset.getView(at, at);
            const hasElmt = yield view.hasByHash(updatePayload.elementHash);
            if (!hasElmt) {
                return validationFailure(`element '${updatePayload.elementHash}' does not exist in set`);
            }
        }
        const contentType = rset.contentType();
        if (contentType !== undefined) {
            const innerFactory = yield rset.getContext().getRegistry().lookup(contentType);
            const innerRObject = yield rset.loadChildObject(innerFactory, updatePayload.elementHash);
            const innerResult = yield innerRObject.validatePayload(updatePayload.updatePayload, at);
            if (!innerResult.valid)
                return wrapValidationFailure("nested element update rejected", innerResult, updatePayload.elementHash);
        }
        return validationOk();
    });
}
